import React, { ReactElement, ReactNode } from 'react'
import styled from 'styled-components'

import { Wrapper, H, P, Image, Divider, Grid } from '@farewill/ui'
import { COLOR, FONT, BORDER_RADIUS, BREAKPOINT } from '@farewill/ui/tokens'
import { screenMax, screenMin } from '@farewill/ui/helpers/responsive'

import { PROBATE_SIMPLE_FEE, PROBATE_ESTATE_ADMIN_FEE } from 'config'
import BreakpointSwitch from 'components/BreakpointSwitch'
import WrapperWithEdge from 'components/WrapperWithEdge'
import {
  PROBATE_FEATURES,
  EXTRA_COSTS,
  EXTRA_INFO,
  STACK_BREAKPOINT,
} from './constants'
import Option from './Option'
import QuoteCTASection from './QuoteCTASection'

const StyledOptions = styled(WrapperWithEdge)`
  position: relative;
  overflow: hidden;
`

const StyledTextureLeft = styled(Image)`
  position: absolute;
  pointer-events: none;
  bottom: 15%;
  left: 0;

  ${screenMax.m`
    display: none;
  `}
`

const StyledTextureRight = styled(Image)`
  position: absolute;
  pointer-events: none;
  top: 50px;
  right: 0;

  ${screenMax.m`
    display: none;
  `};
`

const StyledExtraCosts = styled(Wrapper)`
  & > * {
    display: inline;
  }
`

const StyledExtraInfo = styled(Wrapper)`
  border-radius: ${BORDER_RADIUS.M};
  border-color: ${COLOR.GREY.DARK};

  & > * {
    display: inline;
  }
`

const StyledWrapper = styled(Wrapper)`
  padding-bottom: 0;
`

const StyledCardsWrapper = styled(Grid)`
  align-items: start;
  grid-template-columns: 1fr;
  grid-template-rows: auto;

  ${screenMin[STACK_BREAKPOINT]`
    grid-template-columns: 1fr 1fr;
    grid-template-rows: [header] auto [description] auto [divider] auto [content] auto;
  `}
`

const StyledGridItem = styled.div`
  ${screenMin[STACK_BREAKPOINT]`
    display: grid;
    grid-template-rows: subgrid;
    grid-row: 1 / -1;
  `}
`

const StyledH = styled(H)`
  font-weight: ${FONT.WEIGHT.MEDIUM};
`

const StyledOption = styled(Option)`
  flex-grow: 1;
`

const Options = ({
  description,
  descriptionWidthInColumns = 6,
}: {
  description?: ReactNode
  descriptionWidthInColumns?: number
}): ReactElement | null => (
  <StyledOptions direction="down" lightAccent thickEdge>
    <StyledTextureLeft
      path="textures/sketchy-swoop-yellow-03_k3g7yy"
      width={300}
    />
    <StyledTextureRight path="textures/detail_new_yellow02" width={400} />

    <Wrapper
      container
      containerPaddingBottom={0}
      style={{ position: 'relative' }}
      centeredFromM
    >
      <H tag="h2" size="L" decorative>
        Get specialist probate support
      </H>
      {description && (
        <P
          size="L"
          color={COLOR.BLACK}
          maxWidthInColumns={descriptionWidthInColumns}
          margin={[0, 'auto']}
        >
          {description}
        </P>
      )}
    </Wrapper>

    <Wrapper style={{ position: 'relative' }}>
      <StyledWrapper container>
        <StyledCardsWrapper
          gap={['M']}
          gapFromM={[0, 'M']}
          gapFromL={[0, 'XL']}
        >
          <StyledGridItem>
            <StyledOption
              title="Grant of probate application"
              priceNote="From"
              price={PROBATE_SIMPLE_FEE}
              priceBottomNote="Fixed-fee pricing"
              description="Great if you've got the confidence and time to deal with the rest of the process."
              list={PROBATE_FEATURES.ESSENTIAL_PROBATE}
            >
              <BreakpointSwitch
                breakAt={BREAKPOINT.S}
                belowComponent={<Divider margin={['XS', 0, 'M', 0]} />}
              />
              <StyledExtraInfo
                borderedFromM
                padding={0}
                paddingFromM={['S', 'M']}
                margin={['S', 0, 'M']}
              >
                <StyledH tag="h5" size="XS" color={COLOR.GREY.DARK}>
                  You will need to:
                </StyledH>{' '}
                <P>{EXTRA_INFO.ESSENTIAL_PROBATE}</P>
              </StyledExtraInfo>
              <BreakpointSwitch
                breakAt={BREAKPOINT.S}
                belowComponent={<Divider margin={['M', 0]} />}
              />
              <StyledExtraCosts>
                <StyledH tag="h5" size="XS" color={COLOR.GREY.DARK}>
                  Extra costs:
                </StyledH>{' '}
                <P>{EXTRA_COSTS.ESSENTIAL_PROBATE}</P>
              </StyledExtraCosts>
            </StyledOption>
          </StyledGridItem>

          <StyledGridItem>
            <StyledOption
              title="Our complete probate service"
              priceNote="From"
              price={PROBATE_ESTATE_ADMIN_FEE}
              priceBottomNote="Fixed-fee pricing"
              description="Great if you're busy, worried about making mistakes or if an impartial specialist could help avoid family conflict."
              list={PROBATE_FEATURES.COMPLETE_PROBATE}
            >
              <Divider margin={['S', 0, 'M']} marginFromM={['M', 0]} />
              <StyledExtraCosts>
                <StyledH tag="h5" size="XS" color={COLOR.GREY.DARK}>
                  Extra costs:
                </StyledH>{' '}
                <P>{EXTRA_COSTS.COMPLETE_PROBATE}</P>
              </StyledExtraCosts>
            </StyledOption>
          </StyledGridItem>
        </StyledCardsWrapper>

        <BreakpointSwitch
          breakAt={BREAKPOINT.S}
          belowComponent={<QuoteCTASection />}
          aboveComponent={<QuoteCTASection />}
        />
      </StyledWrapper>
    </Wrapper>
  </StyledOptions>
)

export default Options
